import { EditorContent, useEditor } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { cn } from "utils";
import { Button } from "components/button";
import { useEffect, useMemo, useRef } from "react";

interface TitleEditorProps {
  value: string;
  autoFocus?: boolean;
  disabled?: boolean;

  onChange?(value: string): void;
}

export function TitleEditor({
  value,
  disabled,
  onChange,
  autoFocus,
}: TitleEditorProps) {
  const valueRef = useRef(value);

  const extensions = useMemo(
    () => [
      Document,
      Paragraph.extend({
        addKeyboardShortcuts() {
          return {
            // biome-ignore lint/correctness/noNestedComponentDefinitions: Tiptap keyboard shortcut handler, not a component
            Enter(props) {
              return props.editor.chain().blur().run();
            },
            // biome-ignore lint/correctness/noNestedComponentDefinitions: Tiptap keyboard shortcut handler, not a component
            Escape(props) {
              return props.editor
                .chain()
                .setContent(valueRef.current)
                .blur()
                .run();
            },
          };
        },
      }),
      Text,
    ],
    [],
  );

  const editor = useEditor({
    autofocus: autoFocus,
    extensions,
    content: value,
    editable: !disabled,
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed || !onChange) {
      return;
    }

    const onBlur = () => {
      if (editor.isDestroyed) {
        return;
      }

      const text = editor.getText().trim() || "Untitled";

      editor.chain().setContent(text).run();
      onChange(text);
    };

    editor.on("blur", onBlur);

    return () => {
      editor.off("blur", onBlur);
    };
  }, [editor, onChange]);

  useEffect(() => {
    valueRef.current = value;

    if (!editor || editor.isDestroyed || editor.getText() === value) {
      return;
    }

    editor.chain().setContent(value).run();
  }, [editor, value]);

  return (
    <div className="flex items-center">
      <EditorContent
        editor={editor}
        className={cn(
          "text-4xl font-medium transition px-2 -mx-2",
          editor?.isFocused && "bg-primary-25",
        )}
      />

      {!editor?.isFocused && !disabled && (
        <Button
          label=""
          variant="ghost"
          iconClassName="text-secondary-400"
          testId="button-edit-project-name"
          className="px-3"
          icon="square-pen"
          onClick={() => {
            if (!editor) {
              return;
            }

            const chain = editor.chain().focus("end");

            if (value === "Untitled") {
              chain.selectAll();
            }

            chain.run();
          }}
        />
      )}
    </div>
  );
}
